import  {useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {snowplowTrackEvent} from "utils/snowplow";
import * as reduxSelectors from "../store/application.reducers";
import {
    clearWorkflowsHistory,
    copyRuleWithName, deleteRule,
    getRuleHistory,
} from "components/workflows/workflows.action";
import translate from "components/translations/translations.wrapper";
import WorkflowHistoryPopUp from "components/popup/WorkflowHistoryPopUp.component";
import Confirm from "components/popup/ConfirmActionPopup.component";
import {WorkflowActionsDropdownProps} from "types/workflowActionsDropdown";
import UserInputArea from "components/workflows/UserInputArea.component";
import UserCommentArea from "components/workflows/UserCommentArea.component";
import {useNavigate} from "react-router-dom";
import {PATH_TO_WORKFLOW_DETAILS} from "components/router/paths";
import {DropdownButton} from "components/common/dropdown/DropdownButton.component";
import {IDropdownListItem} from "types/gaiaComponents/dropdownButton";
import { MODAL_ACTIONS } from "utils/constants";
import { launchDarkly } from "utils/launchDarkly";
import { useModalContext } from "components/modals/ModalContext";

function WorkflowsActionsDropdownComponent(props: WorkflowActionsDropdownProps) {
    const dispatch = useDispatch();
    const userConfiguration = useSelector(reduxSelectors.getUsersData);
    const companyId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const deleteUserInputRef = useRef(null);
    const copyUserInputRef = useRef(null);
    const [showWorkflowHistory, setShowWorkflowHistory] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showConfirmCopy, setShowConfirmCopy] = useState(false);
    const navigate = useNavigate();

    const useNewModals = launchDarkly.companyClient?.variation("use-new-modals", false);
    const modalContext = useModalContext();
    const toggleModal = modalContext?.toggleModal;

    const navigateToWorkflowDetails = (idRuleConfiguration: number) => {
        navigate(PATH_TO_WORKFLOW_DETAILS + idRuleConfiguration);
    }

    const onCopyRule = function () {
        const newRuleName = copyUserInputRef.current ? (copyUserInputRef.current as HTMLTextAreaElement).value : "";
        let newRule = Object.assign({}, props.rule);
        newRule.name = newRuleName;
        dispatch(copyRuleWithName(companyId, newRule, navigateToWorkflowDetails, props.copyCallback));
        setShowConfirmCopy(false);
    };

    const onDeleteRule = function () {
        const userComment = deleteUserInputRef.current ? (deleteUserInputRef.current as HTMLTextAreaElement).value : "";
        dispatch(deleteRule(companyId, props.rule.idRuleConfiguration, userComment, props.deleteCallback));
        setShowConfirmDelete(false);
    };

    const onShowRuleHistory = function () {
        snowplowTrackEvent({
            category: "audit trail",
            action: "open_workflow_history",
            label: "rule level workflow history",
            value: props.rule.idRuleConfiguration + "",
        }, userConfiguration);

        if (useNewModals && toggleModal) {
            toggleModal(MODAL_ACTIONS.WORKFLOW_HISTORY, {
                ruleId: props.rule.idRuleConfiguration,
            });
            return;
        }

        setShowWorkflowHistory(true);
        dispatch(getRuleHistory(companyId, props.rule.idRuleConfiguration));
    };

    const onHideRuleHistory = function () {
        setShowWorkflowHistory(false);
        dispatch(clearWorkflowsHistory());
    }

    const openCopyConfirm = () => {
        if (useNewModals && toggleModal) {
            toggleModal(MODAL_ACTIONS.CONFIRM_ACTION, {
                title: props.translate("workflowDetails.confirmDialog.copyTitle", props.rule.name),
                confirmButtonText: "workflowDetails.confirmDialog.copyWorkflow",
                cancelButtonText: "workflowDetails.confirmDialog.cancel",
                handleAction: () => {
                    toggleModal(null);
                    onCopyRule();
                },
                moreContent: (
                    <UserInputArea
                        message={props.translate("workflowDetails.confirmDialog.copyMessage")}
                        inputMessage={props.translate("workflowDetails.confirmDialog.copyInputMessage")}
                        moreInfo={props.translate("workflowDetails.confirmDialog.copyMoreInfo")}
                        defaultValue={props.rule.name + " (1)"}
                        userInput={copyUserInputRef}
                    />
                ),
            });
        } else {
            setShowConfirmCopy(true);
        }
    };

    const openDeleteConfirm = () => {
        if (useNewModals && toggleModal) {
            toggleModal(MODAL_ACTIONS.CONFIRM_ACTION, {
                title: props.translate("workflowDetails.confirmDialog.deleteTitle", props.rule.name),
                confirmButtonColor: "btn-default",
                confirmButtonText: "workflowDetails.confirmDialog.deleteWorkflow",
                cancelButtonText: "workflowDetails.confirmDialog.dontDelete",
                handleAction: () => {
                    toggleModal(null);
                    onDeleteRule();
                },
                moreContent: (
                    <UserCommentArea
                        message={props.rule.active ? props.translate("workflowDetails.confirmDialog.moreDeleteInfo")
                            : props.translate("workflowDetails.confirmDialog.moreDeleteInfoUndone")
                        }
                        inputMessage={props.translate("workflowDetails.confirmDialog.deleteInputMessage")}
                        moreInfo={props.translate("workflowDetails.confirmDialog.moreInfo")}
                        userInput={deleteUserInputRef}
                    />
                ),
            });
        } else {
            setShowConfirmDelete(true);
        }
    };

    const dropdownItems: IDropdownListItem[] = [
        {
            key: "duplicate",
            text: props.translate("workflows.duplicate"),
            onClick: openCopyConfirm,
            disabled: false,
            dataCy: "workflows.duplicate"
        },
        {
            key: "history",
            text: props.translate("workflows.workflowHistoryBtn"),
            onClick: onShowRuleHistory,
            disabled: false,
            dataCy: "workflows.workflowHistoryBtn"
        },
        {
            key: "delete",
            text: props.translate("workflows.delete"),
            onClick: openDeleteConfirm,
            disabled:  props.rule?.readonly,
            dataCy: "workflows.delete"
        }
    ];

    return (
        <div className="workflow-actions">
            {showWorkflowHistory &&
                <WorkflowHistoryPopUp
                    // @ts-ignore
                    closeCallback={onHideRuleHistory}
                    ruleId={props.rule.idRuleConfiguration}
                    translate={props.translate}/>
            }
            {showConfirmDelete && !useNewModals &&
                <Confirm translate={props.translate}
                         handleAction={onDeleteRule}
                         closeCallback={() => {
                             setShowConfirmDelete(false);
                         }}
                         modalType={"modal-warning"}
                         confirmButtonColor={"btn-default"}
                         confirmButtonText={props.translate("workflowDetails.confirmDialog.deleteWorkflow")}
                         cancelButtonText={props.translate("workflowDetails.confirmDialog.dontDelete")}
                         title={props.translate("workflowDetails.confirmDialog.deleteTitle", props.rule.name)}
                         moreContent={
                             <UserCommentArea
                                 message={props.rule.active ? props.translate("workflowDetails.confirmDialog.moreDeleteInfo")
                                     : props.translate("workflowDetails.confirmDialog.moreDeleteInfoUndone")
                                 }
                                 inputMessage={props.translate("workflowDetails.confirmDialog.deleteInputMessage")}
                                 moreInfo={props.translate("workflowDetails.confirmDialog.moreInfo")}
                                 userInput={deleteUserInputRef}
                             />
                         }
                >
                </Confirm>
            }
            {showConfirmCopy && !useNewModals &&
                <Confirm translate={props.translate}
                         handleAction={onCopyRule}
                         closeCallback={() => {
                             setShowConfirmCopy(false);
                         }}
                         modalType={"modal-info"}
                         confirmButtonText={props.translate("workflowDetails.confirmDialog.copyWorkflow")}
                         cancelButtonText={props.translate("workflowDetails.confirmDialog.cancel")}
                         title={props.translate("workflowDetails.confirmDialog.copyTitle", props.rule.name)}
                         moreContent={
                             <UserInputArea
                                 message={props.translate("workflowDetails.confirmDialog.copyMessage")}
                                 inputMessage={props.translate("workflowDetails.confirmDialog.copyInputMessage")}
                                 moreInfo={props.translate("workflowDetails.confirmDialog.copyMoreInfo")}
                                 defaultValue={props.rule.name + " (1)"}
                                 userInput={copyUserInputRef}
                             />}
                >
                </Confirm>
            }
            <DropdownButton
                id="workflow-actions-dropdown"
                type={props.type}
                icon={props.icon}
                position={props.position}
                text={props.icon ? null : props.translate("workflows.moreActions")}
                title={props.translate("workflows.moreActions")}
                items={dropdownItems}
                isEmpty={false}
            />
        </div>
    );
}

const withTranslations = translate(WorkflowsActionsDropdownComponent);
export default withTranslations;